import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { 
  HardHat, Server, Warehouse, Factory, CheckCircle2, 
  ArrowRight, ArrowLeft, Users, Building2, MapPin,
  Loader2, Sparkles, Shield, Zap, Clock, Target
} from 'lucide-react';
import { Progress } from './ui/progress';

const API = process.env.REACT_APP_BACKEND_URL;

const INDUSTRIES = [
  { 
    id: 'construction', 
    name: 'Construction', 
    icon: HardHat, 
    color: 'from-orange-500 to-amber-600',
    description: 'General contractors, subcontractors, builders',
    osha: '29 CFR 1926'
  },
  { 
    id: 'data_center', 
    name: 'Data Center', 
    icon: Server, 
    color: 'from-blue-500 to-cyan-600',
    description: 'Colocation, hyperscale, IT infrastructure',
    osha: '29 CFR 1910, NFPA 75/76'
  },
  { 
    id: 'warehouse', 
    name: 'Warehouse & Logistics', 
    icon: Warehouse, 
    color: 'from-purple-500 to-indigo-600',
    description: 'Distribution centers, 3PL, fulfillment',
    osha: '29 CFR 1910'
  },
  { 
    id: 'manufacturing', 
    name: 'Manufacturing', 
    icon: Factory, 
    color: 'from-green-500 to-emerald-600',
    description: 'Machine shops, assembly, production',
    osha: '29 CFR 1910'
  }
];

const COMPANY_SIZES = [
  { id: 'micro', name: '1-10 employees', icon: '👤', description: 'Micro business' },
  { id: 'small', name: '11-50 employees', icon: '👥', description: 'Small business' },
  { id: 'medium', name: '51-250 employees', icon: '🏢', description: 'Medium business' },
  { id: 'large', name: '250+ employees', icon: '🏙️', description: 'Large business' }
];

const OnboardingWizard = ({ onComplete, onSkip }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    industry: '',
    companySize: '',
    companyName: '',
    locations: 1,
    primaryConcerns: []
  });

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  const handleIndustrySelect = (industryId) => {
    setFormData({ ...formData, industry: industryId });
  };

  const handleSizeSelect = (sizeId) => {
    setFormData({ ...formData, companySize: sizeId });
  };

  const handleNext = () => {
    if (step === 1 && !formData.industry) {
      toast({ title: 'Select Industry', description: 'Please select your industry to continue', variant: 'destructive' });
      return;
    }
    if (step === 2 && !formData.companySize) {
      toast({ title: 'Select Size', description: 'Please select your company size', variant: 'destructive' });
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Activate the selected industry program
      if (formData.industry) {
        await axios.post(`${API}/api/programs/${formData.industry}/activate`, {
          company_name: formData.companyName,
          company_size: formData.companySize,
          locations: formData.locations
        }, { headers });
      }

      // Save onboarding completion to user profile (persists across sessions)
      await axios.post(`${API}/api/auth/onboarding-complete`, {
        industry: formData.industry,
        companySize: formData.companySize,
        companyName: formData.companyName,
        locations: formData.locations,
        programsActivated: [formData.industry]
      }, { headers });

      toast({ 
        title: '🎉 Setup Complete!', 
        description: 'Your safety program is ready. Start with daily inspections.'
      });
      
      if (onComplete) {
        onComplete(formData);
      }
    } catch (error) {
      console.error('Onboarding error:', error);
      toast({ title: 'Error', description: 'Setup failed. You can configure later.', variant: 'destructive' });
      if (onComplete) onComplete(formData);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipWithPersist = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        await axios.post(`${API}/api/auth/onboarding-skip`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Error saving skip status:', error);
    }
    if (onSkip) onSkip();
  };

  const selectedIndustry = INDUSTRIES.find(i => i.id === formData.industry);

  return (
    <div className="fixed inset-0 bg-slate-950/95 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl bg-white shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Sparkles className="w-6 h-6 text-blue-600" />
            <span className="text-sm font-medium text-blue-600">Quick Setup Wizard</span>
          </div>
          <CardTitle className="text-2xl">Get Compliant in 15 Minutes</CardTitle>
          <CardDescription>
            We'll configure the perfect safety program for your business
          </CardDescription>
          <Progress value={progress} className="mt-4 h-2" />
          <p className="text-xs text-slate-500 mt-2">Step {step} of {totalSteps}</p>
        </CardHeader>

        <CardContent className="p-6">
          {/* Step 1: Industry Selection */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-center mb-6">What industry are you in?</h3>
              <div className="grid grid-cols-2 gap-4">
                {INDUSTRIES.map((industry) => {
                  const Icon = industry.icon;
                  const isSelected = formData.industry === industry.id;
                  return (
                    <button
                      key={industry.id}
                      onClick={() => handleIndustrySelect(industry.id)}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50 shadow-lg' 
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`p-2 rounded-lg bg-gradient-to-r ${industry.color}`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{industry.name}</p>
                          <p className="text-xs text-slate-500">{industry.osha}</p>
                        </div>
                        {isSelected && <CheckCircle2 className="w-5 h-5 text-blue-500 ml-auto" />}
                      </div>
                      <p className="text-sm text-slate-600">{industry.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Company Size */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-center mb-6">How big is your company?</h3>
              <div className="grid grid-cols-2 gap-4">
                {COMPANY_SIZES.map((size) => {
                  const isSelected = formData.companySize === size.id;
                  return (
                    <button
                      key={size.id}
                      onClick={() => handleSizeSelect(size.id)}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50 shadow-lg' 
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-2xl">{size.icon}</span>
                        {isSelected && <CheckCircle2 className="w-5 h-5 text-blue-500" />}
                      </div>
                      <p className="font-semibold text-slate-900">{size.name}</p>
                      <p className="text-sm text-slate-500">{size.description}</p>
                    </button>
                  );
                })}
              </div>
              
              {formData.companySize && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    {formData.companySize === 'micro' || formData.companySize === 'small' 
                      ? '✓ Perfect for WSafe AI - designed for SMBs like you!'
                      : '✓ WSafe AI scales with your organization'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Company Details */}
          {step === 3 && (
            <div className="space-y-6">
              <h3 className="font-semibold text-lg text-center mb-6">Tell us about your company</h3>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Company Name</Label>
                  <Input
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    placeholder="Your company name"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Number of Locations/Sites</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.locations}
                    onChange={(e) => setFormData({ ...formData, locations: parseInt(e.target.value) || 1 })}
                    className="mt-1"
                  />
                </div>
              </div>
              
              {selectedIndustry && (
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2 rounded-lg bg-gradient-to-r ${selectedIndustry.color}`}>
                      <selectedIndustry.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold">{selectedIndustry.name} Safety Program</p>
                      <p className="text-xs text-slate-500">Will be activated for your account</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Summary & Activation */}
          {step === 4 && (
            <div className="space-y-6">
              <h3 className="font-semibold text-lg text-center mb-2">Ready to Get Compliant!</h3>
              <p className="text-center text-slate-500 text-sm mb-6">
                Here's what we'll set up for you automatically
              </p>

              <div className="space-y-4">
                {/* What you get */}
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                  <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <Shield className="w-5 h-5" /> Your {selectedIndustry?.name} Package Includes:
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 text-sm text-blue-800">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Daily inspection checklists
                    </div>
                    <div className="flex items-center gap-2 text-sm text-blue-800">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      OSHA-compliant forms
                    </div>
                    <div className="flex items-center gap-2 text-sm text-blue-800">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Training topics & toolbox talks
                    </div>
                    <div className="flex items-center gap-2 text-sm text-blue-800">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      AI hazard scan profiles
                    </div>
                    <div className="flex items-center gap-2 text-sm text-blue-800">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Incident tracking workflow
                    </div>
                    <div className="flex items-center gap-2 text-sm text-blue-800">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      OSHA 300 log integration
                    </div>
                  </div>
                </div>

                {/* Quick stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <Clock className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-slate-900">15 min</p>
                    <p className="text-xs text-slate-500">Setup time</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <Target className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-slate-900">OSHA</p>
                    <p className="text-xs text-slate-500">Compliance tools</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <Zap className="w-6 h-6 text-amber-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-slate-900">AI</p>
                    <p className="text-xs text-slate-500">Powered scans</p>
                  </div>
                </div>

                {/* Summary */}
                <div className="p-3 bg-slate-100 rounded-lg text-sm">
                  <p className="text-slate-700">
                    <strong>Company:</strong> {formData.companyName || 'Your Company'} • 
                    <strong> Size:</strong> {COMPANY_SIZES.find(s => s.id === formData.companySize)?.name || 'N/A'} • 
                    <strong> Sites:</strong> {formData.locations}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-4 border-t">
            <div>
              {step > 1 ? (
                <Button variant="outline" onClick={handleBack}>
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
              ) : (
                <Button variant="ghost" onClick={handleSkipWithPersist} className="text-slate-500">
                  Skip for now
                </Button>
              )}
            </div>
            <div>
              {step < totalSteps ? (
                <Button onClick={handleNext} className="bg-blue-600 hover:bg-blue-700">
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={handleComplete} 
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Activate & Start
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingWizard;
